import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import AddIcon from '@material-ui/icons/Add'
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline'
import FileCopyOutlinedIcon from '@material-ui/icons/FileCopyOutlined'
import GetAppIcon from '@material-ui/icons/GetApp'
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined'
import SearchIcon from '@material-ui/icons/Search'
import VisibilityIcon from '@material-ui/icons/Visibility'
import React, { useCallback, useEffect, useRef, useState } from 'react'

import {
  createUnderageConsent,
  deleteUnderageConsent,
  fetchScopedCentres,
  fetchUnderageConsents,
  fetchUnderageOptions
} from 'api'
import {
  SearchHeadCell,
  useColumnSearch
} from 'app/components/generic/SearchHeadCell'
import constants from 'app/constants'
import { confirm, showToast } from 'app/helpers'
import { useOpsCentre } from 'app/hooks/useOpsCentre'

import {
  downloadUnderagePdf,
  underagePdfUrl
} from '../components/safeguardingPdf'

const ALL_CENTRES = 'all'
const CENTRE_KEY = 'oh.underageConsent.centre'

// What each table column shows, for the search boxes in its heading.
const CONSENT_SEARCH = {
  child: (r) => [r.child_name, r.child_dob].filter(Boolean).join(' '),
  centre: (r) => r.centre_name,
  program: (r) => [r.program, r.grade].filter(Boolean).join(' / '),
  days: (r) => r.days_underage,
  status: (r) =>
    r.status === 'signed'
      ? 'submitted by parent'
      : 'awaiting parent submission',
  parent: (r) => [r.parent_name, r.parent_phone].filter(Boolean).join(' ')
}

const useStyles = makeStyles((theme) => ({
  // Match Newsletter / Event Creative exactly: padded root, plain table,
  // no card box. The new tabs looked like a different product without it.
  root: { padding: theme.spacing(3) },
  head: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(2),
    flexWrap: 'wrap',
    gap: theme.spacing(1)
  },
  filters: { display: 'flex', alignItems: 'center', gap: theme.spacing(1) },
  centre: { minWidth: 200 },
  // No overflow box: it would stop the heading sticking to the window.
  // No fixed minimum: the table fits the screen and the cells wrap, rather than
  // forcing a horizontal scroll that pushed the parent and actions off the edge.
  table: { width: '100%' },
  empty: { padding: theme.spacing(4), textAlign: 'center' },
  // The status is a longer sentence now ("form to be submitted by the parent"),
  // so the pill wraps within a sensible width instead of stretching the column.
  chip: {
    display: 'inline-block',
    borderRadius: 12,
    padding: '2px 10px',
    fontSize: 12,
    maxWidth: 150,
    whiteSpace: 'normal',
    textAlign: 'center',
    lineHeight: 1.3
  },
  signed: { background: '#e8f5e9', color: '#2e7d32' },
  sent: { background: '#fff8e1', color: '#8a6d00' },
  note: { marginTop: theme.spacing(1) },
  // The live-search results sit directly under the search box, like any
  // type-ahead. A thin bordered list, each row a whole clickable target.
  searchWrap: { position: 'relative' },
  results: {
    border: '1px solid #e0e0e0',
    borderRadius: 4,
    marginTop: 4,
    overflow: 'hidden'
  },
  result: {
    padding: '8px 12px',
    cursor: 'pointer',
    borderBottom: '1px solid #f0f0f0',
    '&:last-child': { borderBottom: 'none' },
    '&:hover': { background: '#f5f7ff' }
  },
  resultMeta: { color: theme.palette.text.secondary },
  pickedParent: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
    border: '1px solid #e0e0e0',
    borderRadius: 4,
    padding: theme.spacing(1, 1, 1, 1.5)
  }
}))

/* The centre team raises this form at PRE-REGISTRATION — the child is not in
   Alfred yet, so there is nothing to look up and nothing for them to copy. All
   they hold is the centre and the parent to send the link to. Child name, date
   of birth, programme and grade come back with the parent's signature. */
const BLANK = {
  centre: '',
  parent_name: '',
  parent_phone: '',
  academic_year: ''
}

const parentUrl = (token) => `${window.location.origin}/consent/${token}`

const UnderageConsent = () => {
  const classes = useStyles()

  const [centres, setCentres] = useState([])
  // False once the user is mapped to a centre — "all centres" then means nothing
  // to them, so the option is not offered.
  const [unrestricted, setUnrestricted] = useState(true)
  // Shared by every Centre Operations page and kept across refreshes.
  const [centreUuid, setCentreUuid, pickCentre] = useOpsCentre({
    fallback: ALL_CENTRES,
    legacyKey: CENTRE_KEY
  })
  const [rows, setRows] = useState([])
  const colSearch = useColumnSearch(rows, CONSENT_SEARCH)
  const [loading, setLoading] = useState(false)

  const [formOpen, setFormOpen] = useState(false)
  const [form, setForm] = useState(BLANK)
  const [saving, setSaving] = useState(false)

  const [viewRow, setViewRow] = useState(null)
  const [sopOpen, setSopOpen] = useState(false)
  // The just-created form, shown in a small dialog with its parent link so the
  // team copies and sends it right there — replacing a toast that flashed across
  // the screen on the way to the top corner.
  const [createdRow, setCreatedRow] = useState(null)

  // Live search: the team types a name or a number, and the five best matching
  // PARENTS appear under the box. Parents, not children — at pre-registration
  // the child has no record to find, but the parent usually does.
  const [search, setSearch] = useState('')
  const [results, setResults] = useState([])
  const [searching, setSearching] = useState(false)
  // Set once a parent is picked from the results, so the dialog can show who
  // the form is for instead of two boxes to type into. null = type it by hand,
  // which is what a cold walk-in needs.
  const [picked, setPicked] = useState(null)
  // After a pick we inject the parent's name into the box; this stops that
  // write from immediately firing a fresh search for the name we just chose.
  const skipSearch = useRef(false)
  const [pdfUrl, setPdfUrl] = useState('')

  useEffect(() => {
    let active = true
    const run = async () => {
      try {
        const data = await fetchScopedCentres()
        const list = data?.results || data || []
        if (!active) return
        setCentres(list)
        setUnrestricted(Boolean(data?.unrestricted))
        setCentreUuid((prev) => {
          const allOk = Boolean(data?.unrestricted)
          if (
            prev &&
            ((prev === ALL_CENTRES && allOk) ||
              list.some((c) => c.uuid === prev))
          ) {
            return prev
          }
          return allOk ? ALL_CENTRES : list[0]?.uuid || ''
        })
      } catch (e) {
        showToast('Could not load centres', constants.ERROR)
      }
    }
    run()
    return () => {
      active = false
    }
  }, [])

  const load = useCallback(async () => {
    if (!centreUuid) return
    setLoading(true)
    try {
      const data = await fetchUnderageConsents(centreUuid)
      setRows(Array.isArray(data) ? data : [])
    } catch (e) {
      showToast('Could not load consent forms', constants.ERROR)
    } finally {
      setLoading(false)
    }
  }, [centreUuid])

  useEffect(() => {
    load()
  }, [centreUuid, load])

  const setField = (key) => (e) => {
    const v = e.target.value
    setForm((f) => ({ ...f, [key]: v }))
  }

  const openPdf = async (row) => {
    setViewRow(row)
    setPdfUrl(await underagePdfUrl(row))
  }

  const closePdf = () => {
    if (pdfUrl) URL.revokeObjectURL(pdfUrl)
    setPdfUrl('')
    setViewRow(null)
  }

  const openForm = async () => {
    setSearch('')
    setResults([])
    setPicked(null)
    // Centre is selected automatically — never frozen. It defaults to the centre
    // the team is already viewing in the list filter, so they don't re-pick it;
    // a parent chosen from search then overrides it with the parent's own centre.
    // When the team is on "all centres" there is nothing to infer, so it falls
    // back to the "select centre" prompt. Either way the dropdown stays editable.
    setForm({
      ...BLANK,
      centre: centreUuid && centreUuid !== ALL_CENTRES ? centreUuid : ''
    })
    setFormOpen(true)
    try {
      const opts = await fetchUnderageOptions()
      // The academic year is whichever year TODAY falls inside — the form is
      // filed for the year it is being filed in, so nobody should be typing it.
      setForm((f) => ({ ...f, academic_year: opts?.academic_year || '' }))
    } catch (e) {
      showToast('Could not load the academic year', constants.ERROR)
    }
  }

  /* Search-as-you-type by name OR number, over PARENTS. Picking beats typing —
     a mistyped number is a link that reaches nobody — but a walk-in enquiring
     for the first time is in no system at all, so typing stays available.
     Debounced so a keystroke is not a request. */
  useEffect(() => {
    if (!formOpen) return undefined
    if (skipSearch.current) {
      skipSearch.current = false
      setResults([])
      setSearching(false)
      return undefined
    }
    const q = (search || '').trim()
    if (q.length < 2) {
      setResults([])
      setSearching(false)
      return undefined
    }
    let active = true
    setSearching(true)
    const handle = setTimeout(async () => {
      try {
        const opts = await fetchUnderageOptions(undefined, undefined, q)
        if (active) setResults(opts?.parents || [])
      } catch (e) {
        if (active) setResults([])
      } finally {
        if (active) setSearching(false)
      }
    }, 300)
    return () => {
      active = false
      clearTimeout(handle)
    }
  }, [search, formOpen])

  const pickParent = (p) => {
    // Injecting the name back into the search box would otherwise trigger a new
    // search for the parent we just chose — skip that one round.
    skipSearch.current = true
    setSearch(p.parent_name || '')
    setResults([])
    setPicked(p)
    setForm((f) => ({
      ...f,
      parent_name: p.parent_name || '',
      parent_phone: p.parent_phone || '',
      // The parent's own centre, so the team doesn't re-pick it. Falls back to
      // whatever was already chosen when the parent has no centre on record.
      centre: p.centre || f.centre
    }))
  }

  const unpickParent = () => {
    setPicked(null)
    setSearch('')
    setResults([])
    setForm((f) => ({ ...f, parent_name: '', parent_phone: '' }))
  }

  const canSave =
    form.centre && form.parent_name.trim() && form.parent_phone.trim()

  const save = async () => {
    if (!canSave) {
      showToast(
        "Centre, parent's name and parent's number are required",
        constants.ERROR
      )
      return
    }
    setSaving(true)
    try {
      const created = await createUnderageConsent(form)
      setFormOpen(false)
      load()
      // Hand the parent link straight to the team in a dialog they can copy from,
      // rather than a toast telling them to go find it. A form with no token to
      // send is nothing to celebrate, so only then fall back to a plain toast.
      if (created && created.token) {
        setCreatedRow(created)
      } else {
        showToast('Consent form created', constants.SUCCESS)
      }
    } catch (e) {
      showToast('Could not create the consent form', constants.ERROR)
    } finally {
      setSaving(false)
    }
  }

  /* The link is already in state (it came with the list), so the clipboard write
     happens on the user's own click with no await in front of it — an await here
     is what makes the browser refuse the write. */
  const copyLink = (row) => {
    const url = parentUrl(row.token)
    const done = () => showToast('Parent link copied', constants.SUCCESS)
    try {
      if (navigator.clipboard && window.isSecureContext) {
        navigator.clipboard.writeText(url).then(done)
        return
      }
      const ta = document.createElement('textarea')
      ta.value = url
      ta.style.position = 'fixed'
      ta.style.opacity = '0'
      document.body.appendChild(ta)
      ta.select()
      document.execCommand('copy')
      ta.remove()
      done()
    } catch (e) {
      showToast('Could not copy the link', constants.ERROR)
    }
  }

  // Deleting a consent form throws away its parent link too, so ask first
  // rather than dropping the row on a single click of a small icon.
  const confirmRemove = (row) => {
    confirm(
      () => remove(row),
      'This deletes the consent form and the parent link it generated. This cannot be undone.',
      undefined,
      { title: 'Delete this consent form?', confirmButtonText: 'Delete' }
    )
  }

  const remove = async (row) => {
    try {
      await deleteUnderageConsent(row.uuid)
      showToast('Deleted', constants.SUCCESS)
      load()
    } catch (e) {
      showToast(
        e?.response?.status === 409
          ? 'A signed consent cannot be deleted'
          : 'Could not delete',
        constants.ERROR
      )
    }
  }

  return (
    <Box className={classes.root}>
      <Box className={classes.head}>
        <Box>
          <Box display="flex" alignItems="center">
            <Typography variant="h5">Underage Consent</Typography>
            <Tooltip title="how this works">
              <IconButton size="small" onClick={() => setSopOpen(true)}>
                <InfoOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        <Box className={classes.filters}>
          <FormControl
            variant="outlined"
            size="small"
            className={classes.centre}
          >
            <InputLabel>centre</InputLabel>
            <Select
              label="centre"
              value={centreUuid}
              onChange={(e) => pickCentre(e.target.value)}
            >
              {unrestricted && (
                <MenuItem value={ALL_CENTRES}>all centres</MenuItem>
              )}
              {centres.map((c) => (
                <MenuItem key={c.uuid} value={c.uuid}>
                  {c.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={openForm}
          >
            new consent form
          </Button>
        </Box>
      </Box>

      <Box>
        {loading ? (
          <Box className={classes.empty}>
            <CircularProgress size={24} />
          </Box>
        ) : rows.length === 0 ? (
          <Box className={classes.empty}>
            <Typography variant="body2" color="textSecondary">
              no consent forms yet.
            </Typography>
          </Box>
        ) : (
          <Table size="small" className={classes.table}>
            <TableHead>
              <TableRow>
                <SearchHeadCell label="child" search={colSearch.box('child')} />
                <SearchHeadCell
                  label="centre"
                  search={colSearch.box('centre')}
                />
                <SearchHeadCell
                  label="program / grade"
                  search={colSearch.box('program')}
                />
                <SearchHeadCell
                  label="days underage"
                  align="right"
                  search={colSearch.box('days')}
                />
                <SearchHeadCell
                  label="status"
                  search={colSearch.box('status')}
                />
                <SearchHeadCell
                  label="parent"
                  search={colSearch.box('parent')}
                />
                <SearchHeadCell label="actions" align="right" />
              </TableRow>
            </TableHead>
            <TableBody>
              {!colSearch.rows.length && (
                <TableRow>
                  <TableCell colSpan={7} className={classes.empty}>
                    no forms match the search.
                  </TableCell>
                </TableRow>
              )}
              {colSearch.rows.map((r) => (
                <TableRow key={r.uuid} hover>
                  {/* Blank until the parent fills it in — the child is not in
                      Alfred when the form is raised, so an unsigned row honestly
                      has no name to show. */}
                  <TableCell>
                    {r.child_name ? (
                      <b>{r.child_name}</b>
                    ) : (
                      <Typography variant="body2" color="textSecondary">
                        <i>not filled in yet</i>
                      </Typography>
                    )}
                    {r.child_dob ? (
                      <Typography
                        variant="caption"
                        display="block"
                        color="textSecondary"
                      >
                        dob {r.child_dob}
                      </Typography>
                    ) : null}
                  </TableCell>
                  <TableCell>{r.centre_name}</TableCell>
                  <TableCell>
                    {r.program || '—'}
                    {r.grade ? ` / ${r.grade}` : ''}
                  </TableCell>
                  <TableCell align="right">
                    {r.days_underage === null || r.days_underage === undefined
                      ? '—'
                      : r.days_underage}
                  </TableCell>
                  <TableCell>
                    <span
                      className={`${classes.chip} ${
                        r.status === 'signed' ? classes.signed : classes.sent
                      }`}
                    >
                      {r.status === 'signed'
                        ? 'submitted by parent'
                        : 'awaiting parent submission'}
                    </span>
                  </TableCell>
                  {/* The parent is who an unsigned row is actually about — it
                      is the only name on it until they sign. */}
                  <TableCell>
                    {r.parent_name || '—'}
                    {r.parent_phone ? (
                      <Typography
                        variant="caption"
                        display="block"
                        color="textSecondary"
                      >
                        {r.parent_phone}
                      </Typography>
                    ) : null}
                  </TableCell>
                  <TableCell align="right">
                    {r.status !== 'signed' ? (
                      <Tooltip title="copy the parent's link">
                        <IconButton size="small" onClick={() => copyLink(r)}>
                          <FileCopyOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    ) : null}
                    <Tooltip title="view">
                      <IconButton size="small" onClick={() => openPdf(r)}>
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="download pdf">
                      <IconButton
                        size="small"
                        onClick={() => downloadUnderagePdf(r)}
                      >
                        <GetAppIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {r.status !== 'signed' ? (
                      <Tooltip title="delete">
                        <IconButton
                          size="small"
                          onClick={() => confirmRemove(r)}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    ) : null}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Box>

      <Dialog
        open={formOpen}
        onClose={() => setFormOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>new underage consent form</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            this is raised before the child is registered, so alfred only needs
            the centre and the parent to send the link to. the child&apos;s
            name, date of birth and grade come back with the parent&apos;s
            signature.
          </Typography>

          {picked ? (
            /* Picked from Alfred: show who, not two boxes to re-type. */
            <Box className={classes.pickedParent} mt={1}>
              <Box>
                <Typography variant="body2">
                  <b>{picked.parent_name}</b>
                </Typography>
                <Typography variant="caption" className={classes.resultMeta}>
                  {picked.parent_phone || 'no number on file'}
                  {picked.centre_name ? ` · ${picked.centre_name}` : ''}
                </Typography>
              </Box>
              <Button size="small" onClick={unpickParent}>
                change
              </Button>
            </Box>
          ) : (
            <>
              <Box className={classes.searchWrap} mt={1}>
                <TextField
                  label="search parent by name or number"
                  variant="outlined"
                  size="small"
                  fullWidth
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  autoComplete="off"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        {searching ? (
                          <CircularProgress size={16} />
                        ) : (
                          <SearchIcon fontSize="small" color="disabled" />
                        )}
                      </InputAdornment>
                    )
                  }}
                />
                {results.length > 0 ? (
                  <Box className={classes.results}>
                    {results.map((p) => (
                      <Box
                        key={p.parent}
                        className={classes.result}
                        onClick={() => pickParent(p)}
                      >
                        <Typography variant="body2">
                          <b>{p.parent_name}</b>
                        </Typography>
                        <Typography
                          variant="caption"
                          className={classes.resultMeta}
                        >
                          {p.parent_phone || 'no number on file'}
                          {p.centre_name ? ` · ${p.centre_name}` : ''}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                ) : search.trim().length >= 2 && !searching ? (
                  <Typography
                    variant="caption"
                    color="textSecondary"
                    style={{ display: 'block', marginTop: 6 }}
                  >
                    no matching parent — type their name and number below
                  </Typography>
                ) : null}
              </Box>

              {/* A first-time walk-in is in no system at all, so the two fields
                  the link actually needs stay typeable. */}
              <Box display="flex" style={{ gap: 16 }} flexWrap="wrap" mt={2}>
                <TextField
                  label="parent's name *"
                  variant="outlined"
                  size="small"
                  value={form.parent_name}
                  onChange={setField('parent_name')}
                  style={{ minWidth: 220 }}
                />
                <TextField
                  label="parent's number *"
                  variant="outlined"
                  size="small"
                  value={form.parent_phone}
                  onChange={setField('parent_phone')}
                  helperText="where you'll send the link"
                  style={{ minWidth: 220 }}
                />
              </Box>
            </>
          )}

          <Box display="flex" style={{ gap: 16 }} flexWrap="wrap" mt={1}>
            <FormControl
              variant="outlined"
              size="small"
              style={{ minWidth: 220 }}
            >
              <InputLabel shrink>centre *</InputLabel>
              <Select
                label="centre *"
                value={form.centre}
                onChange={setField('centre')}
                displayEmpty
              >
                <MenuItem value="">
                  <em>select centre</em>
                </MenuItem>
                {centres.map((c) => (
                  <MenuItem key={c.uuid} value={c.uuid}>
                    {c.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Box display="flex" style={{ gap: 16 }} flexWrap="wrap" mt={2}>
            <TextField
              label="academic year"
              variant="outlined"
              size="small"
              value={form.academic_year || '—'}
              helperText="from today's date"
              InputProps={{ readOnly: true }}
              style={{ minWidth: 180 }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFormOpen(false)}>cancel</Button>
          <Button
            variant="contained"
            color="primary"
            onClick={save}
            disabled={saving}
          >
            {saving ? 'creating…' : 'create & get parent link'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Straight after creating a form: its parent link, ready to copy and
          send. The row is already in the list behind this — closing it loses
          nothing, the copy icon on the row gives the same link again. */}
      <Dialog
        open={Boolean(createdRow)}
        onClose={() => setCreatedRow(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Consent form created</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" gutterBottom>
            Send this link to the parent. They open it on their phone, fill in
            the child&apos;s details and sign — the row then turns to signed.
          </Typography>
          <TextField
            variant="outlined"
            size="small"
            fullWidth
            value={createdRow ? parentUrl(createdRow.token) : ''}
            InputProps={{ readOnly: true }}
            onFocus={(e) => e.target.select()}
          />
        </DialogContent>
        <DialogActions>
          <Button
            startIcon={<FileCopyOutlinedIcon />}
            onClick={() => createdRow && copyLink(createdRow)}
          >
            copy link
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={() => setCreatedRow(null)}
          >
            done
          </Button>
        </DialogActions>
      </Dialog>

      {/* The document itself — what you check on screen is what prints. */}
      <Dialog
        open={Boolean(viewRow)}
        onClose={closePdf}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {viewRow?.child_name || viewRow?.parent_name || 'unfilled'} — underage
          consent
        </DialogTitle>
        <DialogContent dividers style={{ padding: 0 }}>
          {pdfUrl ? (
            <iframe
              src={pdfUrl}
              title="underage consent"
              style={{ width: '100%', height: '70vh', border: 0 }}
            />
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button
            startIcon={<GetAppIcon />}
            onClick={() => viewRow && downloadUnderagePdf(viewRow)}
          >
            download pdf
          </Button>
          <Button onClick={closePdf} color="primary">
            close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={sopOpen}
        onClose={() => setSopOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>How Underage Consent works</DialogTitle>
        <DialogContent>
          <Typography variant="body2" gutterBottom>
            This is the consent letter and parent undertaking for a child being
            enrolled below the age policy. <b>The parent signs it</b>, and
            parents do not have Alfred logins — so it works like this:
          </Typography>
          <ol style={{ paddingLeft: 18, lineHeight: 1.7 }}>
            <li>
              Press <b>new consent form</b>, pick the centre and search for the
              parent — or type their name and number if they are new to us. The
              child&apos;s details are <b>not</b> asked for here: this is raised
              at pre-registration, before the child exists in Alfred.
            </li>
            <li>
              Copy the <b>parent link</b> (the copy icon on the row) and send it
              on WhatsApp.
            </li>
            <li>
              The parent opens it on their phone, fills in every question —
              child&apos;s name, date of birth, the grade they want — reads the
              undertaking, signs with their finger, and submits. The row fills
              itself in and turns to <b>signed</b>.
            </li>
          </ol>
          <Typography variant="body2" gutterBottom>
            The link is private to that one child, and it stops working once the
            form is signed. A signed consent cannot be edited or deleted.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSopOpen(false)} color="primary">
            Got it
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default UnderageConsent
